import { CallPayload, CONTRACT_VERSION, DEFAULT_LADDER, } from "@zoidberg/contract";
import { solveWake, formatHHMM } from "./wake/solver.js";
import { redactEvent, effectiveLevel } from "./redaction/redact.js";
import { zonedWallToInstant } from "./time/zoned.js";
/** "10 AM", "9:20 AM" — human clock label for opaque/echo copy. */
function clock12(min) {
    const wrapped = ((min % 1440) + 1440) % 1440;
    let h = Math.floor(wrapped / 60);
    const m = wrapped % 60;
    const ampm = h < 12 ? "AM" : "PM";
    h = h % 12 || 12;
    return m === 0 ? `${h} ${ampm}` : `${h}:${m.toString().padStart(2, "0")} ${ampm}`;
}
export function compileDayPlan(input) {
    const ladder = input.ladder ?? DEFAULT_LADDER;
    const sorted = [...input.events].sort((a, b) => a.startMin - b.startMin);
    const firstOut = sorted.find((e) => e.outOfHouse);
    const solved = solveWake({
        firstEvent: firstOut
            ? {
                startMin: firstOut.startMin,
                travelMin: firstOut.travelMin ?? 0,
                parkingBufferMin: firstOut.parkingBufferMin ?? 0,
            }
            : undefined,
        bedtimeMin: input.bedtimeMin,
        routineComfortMin: input.routineComfortMin,
        routineMinimumMin: input.routineMinimumMin,
        emptyMorningFallbackMin: input.emptyMorningFallbackMin,
        emptyMorningLatestMin: input.emptyMorningLatestMin,
    });
    const labelFor = (e) => redactEvent({ title: e.title, startLabel: clock12(e.startMin), important: e.important }, effectiveLevel(input.redactionLevel, e.calendarOpaque ?? false), { knownEntities: input.knownEntities });
    // Shape of the day: ≤4 beats (one-thing-at-a-time, §6.1).
    const beats = sorted.slice(0, 4).map((e) => `${labelFor(e)} at ${clock12(e.startMin)}`);
    if (solved.note)
        beats.unshift(solved.note); // surface the short-sleep reality first
    const cappedBeats = beats.slice(0, 5);
    // First event with leave-by.
    const scriptFirstEvent = firstOut
        ? {
            label: labelFor(firstOut),
            startsAt: formatHHMM(firstOut.startMin),
            leaveBy: solved.leaveByMin !== null ? formatHHMM(solved.leaveByMin) : null,
        }
        : null;
    // Task echo — the dynamic verification. Prefer leave-by; else the first start.
    // expectedAnswer is the spoken clock form the agent listens for; acceptable
    // phrases add the canonical HH:MM so a paraphrase still grades as correct.
    const echoEvent = firstOut ?? sorted[0];
    const taskEcho = firstOut
        ? {
            prompt: `First on deck: ${labelFor(firstOut)} at ${clock12(firstOut.startMin)}, leave by ${clock12(solved.leaveByMin)}.`,
            challenge: "What time are you out the door?",
            expectedAnswer: clock12(solved.leaveByMin),
            acceptablePhrases: [formatHHMM(solved.leaveByMin)],
        }
        : echoEvent
            ? {
                prompt: `First thing today: ${labelFor(echoEvent)} at ${clock12(echoEvent.startMin)}.`,
                challenge: "What's the first thing on your plate today?",
                expectedAnswer: clock12(echoEvent.startMin),
                acceptablePhrases: [formatHHMM(echoEvent.startMin)],
            }
            : {
                prompt: "Nothing fixed this morning, but it's time to be up.",
                challenge: "Tell me one thing you want to get done today.",
                expectedAnswer: "anything",
                acceptablePhrases: [],
            };
    const firstAction = {
        text: firstOut
            ? `Feet on floor, water, shower. First up is ${labelFor(firstOut)} — leave by ${clock12(solved.leaveByMin)}.`
            : "Feet on floor, water, coffee. Open the app for today's shape.",
    };
    const payload = {
        contractVersion: CONTRACT_VERSION,
        date: input.date,
        generatedAt: input.generatedAt,
        expiresAt: input.expiresAt,
        isFallback: false,
        wake: {
            timezone: input.timezone,
            wakeTime: zonedWallToInstant(input.date, solved.wakeMin, input.timezone),
            hardWake: zonedWallToInstant(input.date, solved.hardWakeMin, input.timezone),
            wakeExplanation: solved.explanation,
            phones: input.phones,
            persona: input.persona,
            redactionLevel: input.redactionLevel,
        },
        escalation: ladder,
        script: {
            greeting: "Morning, Alie.",
            weather: input.weather ?? null,
            beats: cappedBeats,
            firstEvent: scriptFirstEvent,
            prepBeforeNoon: [],
            taskEcho,
            firstAction,
        },
    };
    // Validate at the boundary — a malformed payload must never reach the worker.
    return CallPayload.parse(payload);
}
//# sourceMappingURL=compile.js.map